import { useEffect, useMemo, useRef, useState } from "react";
import DataContainer from "../services/DataContainer";
import {
  defaultRenderInfo,
  ImageRequest,
  ImageStatus,
  RenderInfo,
} from "./useRenderOnDemand.types";
import { Buffer } from "buffer";


const useRenderOnDemand = (specification: ImageRequest, ignoreTimeout?:boolean, extraHeaders?: Record<string, string>): RenderInfo => {
  const interval = useRef<ReturnType<typeof setTimeout> | undefined>();

  const [cache, setCache] = useState<Record<string, RenderInfo>>({});

  const getImages = (hash: string) => {
    let info = cache[hash];

    if(!info) {
      info = { ...defaultRenderInfo };
      setCache({ ...cache, [hash]: info});
    }

    return info;
  };

  const setImages = (hash: string, info: RenderInfo) => {
    setCache({ ...cache, [hash]: info });
  };

  // Check if configuration is valid
  const hasValidConfiguration = specification?.configuration?.productId && specification.configuration.productId > 0;

  let maxTries = 14;
  const buffer = Buffer.from(JSON.stringify(specification), "utf8");
  const hash = buffer.toString("base64");

  const imageInfo = useMemo(() => getImages(hash), [cache, hash]);

  const prevHash = useRef<string>(hash);

  useEffect(() => {
    if (!hasValidConfiguration) {
      return;
    }
    if (imageInfo.status === ImageStatus.Timeout && hash != prevHash.current) {
      const newInfo = { ...imageInfo };
      newInfo.status = ImageStatus.None;
      newInfo.urlAssets.length = 0;
      setImages(hash, newInfo);
    }
  }, [hash, imageInfo.status]);

  useEffect(() => {
    prevHash.current = hash;
  }, [hash]);

  useEffect(() => {
    if (!hasValidConfiguration) {
      return;
    }
    if (!imageInfo.urlAssets.length) {
      if (imageInfo.status == ImageStatus.None) {
        const request = specification;
        DataContainer.getRender(request, extraHeaders)
          .then((status) => {
            const data = status.data;
            if (data) {
              const info = {
                ...data,
                tries: 0
              };
              setImages(hash, info);
            }
          })
          .catch((error: any) => {
            console.error(error);
          });

        const newInfo = { ...imageInfo, status: ImageStatus.Fetching };
        setImages(hash, newInfo);
      } else if (imageInfo.status == ImageStatus.Queued) {
        if (!hasValidConfiguration) {
          return;
        }

        let timeoutPeriod = 500;
        if (imageInfo.tries >= 15) {
          if (imageInfo.tries == 15) {
            timeoutPeriod = 2000;
          }
          maxTries = 28;
        }
        if (imageInfo.tries == 1) {
          timeoutPeriod = 2000;
          maxTries = 14;
        }
        interval.current = setTimeout(() => {
          DataContainer.checkStatus(imageInfo)
            .then((status) => {
              const timedOut = (imageInfo.tries + 1 > maxTries) && (status.data.status !== ImageStatus.Ready) && !ignoreTimeout;
              const info = {
                ...status.data,
                status: timedOut ? ImageStatus.Timeout : status.data.status === "error" ? ImageStatus.Error : status.data.status,
                tries: imageInfo.tries + 1
              };
              setImages(hash, info);
            })
            .catch((error: any) => {
              console.error(error);
            });
        }, timeoutPeriod);
      }
    }
    return () => {
      clearTimeout(interval.current);
    };
  }, [imageInfo, hasValidConfiguration]);

  return imageInfo;
};

export default useRenderOnDemand;